import React, { useState, useEffect, useMemo, useRef } from 'react'
import './MiscellaneousPublicSearch.scss'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import SearchIcon from '@tecuity/sos-icons/react/search-heavy'
import EmptySearchPlaceholder from './EmptySearchPlaceholder'
import { Body } from '../Shell/Shell'
import { DumbField } from 'components/Wizard2/Field'
import {
  TextInput,
  RadioInput,
  Select,
} from 'components/Wizard2/fieldComponents/Form'
import DateRange from 'components/Wizard2/fieldComponents/DateRange'
import { Spinner } from '../Loaders/Loaders'
import MiscRecordsModuleDropdown from '../MiscRecords/MiscRecordsModuleDropdown'
import MiscellaneousRecordTable from '../MiscRecords/MiscellaneousRecordTable'
import MiscellaneousRecordDrawer from '../MiscRecords/MiscellaneousRecordDrawer'
import {
  fetchMiscPublicSearchTemplate,
  fetchMiscPublicSearchResults,
  fetchMiscRecordDrawer,
  setMiscPublicSearchFilter,
  setMiscPublicSearchAdvancedFilter,
  resetMiscPublicSearchFilters,
  setMiscPublicSearchAdvancedOpen,
  setMiscPublicSearchSelectedRecordId,
} from '../MiscRecords/MiscellaneousPublicSearchActions'

const SEARCH_TYPE_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'startswith', label: 'Starts With' },
]

const MiscellaneousPublicSearch = ({ sublinks }) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const {
    template,
    templateLoading,
    items,
    totalCount,
    loading,
    error,
    filters,
    advancedOpen,
    selectedRecordId,
    drawerData,
    drawerLoading,
  } = useSelector((state) => state.miscPublicSearch || {})

  const [searchInput, setSearchInput] = useState('')

  const advancedPanelRef = useRef(null)
  const advancedToggleRef = useRef(null)

  const sharedTabIndex = advancedOpen ? undefined : -1

  const sourceTypeParam = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location])

  const sublink = useMemo(() => {
    return (sublinks || []).find((x) => x.parameter === sourceTypeParam) || {}
  }, [sublinks, sourceTypeParam])

  const modules = useMemo(() => {
    return (sublink.MiscRecordsModules || []).filter((m) => m.HasPublicSearch)
  }, [sublink])

  const selectedModuleId = useSelector((state) => state.app.miscRecordsSelectedModules[sourceTypeParam])

  const selectedModule = useMemo(() => {
    return modules.find((m) => m.MiscellaneousModuleId == selectedModuleId) || null
  }, [modules, selectedModuleId])

  useEffect(() => {
    if (selectedModuleId) {
      dispatch(resetMiscPublicSearchFilters())
      setSearchInput('')
      dispatch(fetchMiscPublicSearchTemplate(selectedModuleId))
    }
  }, [selectedModuleId])

  const placeholderText = (template && template.PlaceholderText) || 'Search records'
  const descriptionMarkdown = template && template.DescriptionMarkdown
  const searchIsValid = searchInput.length >= 2

  const handleSearch = () => {
    if (!searchIsValid || !selectedModuleId) return
    dispatch(setMiscPublicSearchFilter('search', searchInput))
    dispatch(fetchMiscPublicSearchResults(selectedModuleId))
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchIsValid) {
      handleSearch()
    }
  }

  const handleClearFilters = () => {
    setSearchInput('')
    dispatch(resetMiscPublicSearchFilters())
  }

  const handleRowClick = (row) => {
    const nextId = selectedRecordId === row.RecordId ? null : row.RecordId
    dispatch(setMiscPublicSearchSelectedRecordId(nextId))
    if (nextId !== null) {
      dispatch(fetchMiscRecordDrawer(nextId))
    }
  }

  const handleDismissDrawer = () => {
    dispatch(setMiscPublicSearchSelectedRecordId(null))
  }

  const handleToggleAdvanced = () => {
    const nextOpen = !advancedOpen
    dispatch(setMiscPublicSearchAdvancedOpen(nextOpen))
    if (nextOpen) {
      setTimeout(() => {
        if (advancedPanelRef.current) {
          const focusable = advancedPanelRef.current.querySelector(
            'input:not([disabled]), button:not([disabled]), select:not([disabled])'
          )
          if (focusable) {
            focusable.focus()
          }
        }
      }, 0)
    } else {
      setTimeout(() => {
        if (advancedToggleRef.current) {
          advancedToggleRef.current.focus()
        }
      }, 0)
    }
  }

  const selectedRecord = useMemo(() => {
    if (!selectedRecordId || !items) return null
    return items.find((r) => r.RecordId === selectedRecordId) || null
  }, [selectedRecordId, items])

  const renderAdvancedField = (field) => {
    const dataType = field.DataType
    const fieldName = field.FieldName
    const advancedFilters = (filters && filters.advancedFilters) || {}

    if (dataType === 'String') {
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="text">
          <RadioInput
            fieldId={fieldName + '__comparator'}
            name={fieldName + '__comparator'}
            options={SEARCH_TYPE_OPTIONS}
            value={advancedFilters[fieldName + '__comparator'] || 'contains'}
            onChange={(val) => dispatch(setMiscPublicSearchAdvancedFilter(fieldName + '__comparator', val))}
            tabIndex={sharedTabIndex}
          />
          <TextInput
            fieldId={fieldName}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => dispatch(setMiscPublicSearchAdvancedFilter(fieldName, val))}
            onBlur={() => {}}
            width="100%"
            tabIndex={sharedTabIndex}
          />
        </DumbField>
      )
    }

    if (dataType === 'Name' || dataType === 'Address' || dataType === 'PhoneNumber') {
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="text">
          <TextInput
            fieldId={fieldName}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => dispatch(setMiscPublicSearchAdvancedFilter(fieldName, val))}
            onBlur={() => {}}
            width="100%"
            tabIndex={sharedTabIndex}
          />
        </DumbField>
      )
    }

    if (dataType === 'Bool') {
      const boolOptions = [
        { value: '', label: 'Any' },
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ]
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="select">
          <Select
            fieldId={fieldName}
            options={boolOptions}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => dispatch(setMiscPublicSearchAdvancedFilter(fieldName, val || ''))}
            onBlur={() => {}}
            width="100%"
            tabIndex={sharedTabIndex}
          />
        </DumbField>
      )
    }

    if (dataType === 'Date' || dataType === 'DateTime') {
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="DateRange" ownsLabel>
          <div className="date-range-wrapper">
            <DateRange
              fieldId={fieldName}
              label={field.DisplayName}
              startLabel="Start"
              endLabel="End"
              ownsLabel
              value={{
                start: advancedFilters[fieldName + 'Start'] || '',
                end: advancedFilters[fieldName + 'End'] || '',
              }}
              onChange={(val) => {
                dispatch(setMiscPublicSearchAdvancedFilter(fieldName + 'Start', (val && val.start) || null))
                dispatch(setMiscPublicSearchAdvancedFilter(fieldName + 'End', (val && val.end) || null))
              }}
              onBlur={() => {}}
              tabIndex={sharedTabIndex}
            />
          </div>
        </DumbField>
      )
    }

    if (dataType === 'LookupType') {
      let lookupOptions = []
      try {
        lookupOptions = JSON.parse(field.LookupTypeValuesJson || '[]')
      } catch (e) {
        lookupOptions = []
      }
      if (!Array.isArray(lookupOptions)) {
        lookupOptions = []
      }

      const selectOptions = [{ value: '', label: 'Any' }].concat(
        lookupOptions.map((item) => ({ value: String(item.value), label: item.label }))
      )

      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="select">
          <Select
            fieldId={fieldName}
            options={selectOptions}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => dispatch(setMiscPublicSearchAdvancedFilter(fieldName, val || ''))}
            onBlur={() => {}}
            width="100%"
            tabIndex={sharedTabIndex}
          />
        </DumbField>
      )
    }

    return null
  }

  return (
    <Body showDrawer={!!selectedRecordId}>
      <div className="misc-public-search">
        <div className="table-controls-wrapper">
          <div className="toolbar">
            <div className="misc-public-search-controls">
              <div className="misc-search-module">
                <div className="misc-search-module-inner">
                  <MiscRecordsModuleDropdown
                    modules={modules}
                    sourceTypeParam={sourceTypeParam}
                    showLabel={true}
                  />
                </div>
              </div>

              {templateLoading ? (
                <div className="reports-spinner animated fadeIn">
                  <Spinner size="medium" themeColor="secondary" />
                </div>
              ) : (
                <>
              {selectedModule && (
                <h2 className="misc-public-search-heading">
                  {selectedModule.DisplayName + ' Search'}
                </h2>
              )}

              {descriptionMarkdown && (
                <ReactMarkdown className="markdown description" rehypePlugins={[rehypeRaw]}>
                  {descriptionMarkdown}
                </ReactMarkdown>
              )}

              <div className="filter-wrapper">
                <div className={'search-input-wrapper' + (advancedOpen ? ' advanced-open' : '')}>
                  <input
                    type="text"
                    className="filter"
                    placeholder={placeholderText}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    aria-label="Search"
                  />
                  {!advancedOpen && (
                    <button
                      type="button"
                      className="btn btn-primary search-button"
                      aria-label="Search"
                      aria-disabled={!searchIsValid}
                      disabled={!searchIsValid}
                      onClick={handleSearch}
                    >
                      <SearchIcon />
                    </button>
                  )}
                </div>
              </div>

              <div
                ref={advancedPanelRef}
                className={"search-advanced-parameters search-advanced-parameters wizard" + (advancedOpen ? ' open' : '')}
              >
                  <DumbField label="Search Type" fieldId="comparator" type="radio" ownsLabel>
                    <RadioInput
                      fieldId="comparator"
                      name="comparator"
                      label="Search Type"
                      ownsLabel
                      options={SEARCH_TYPE_OPTIONS}
                      value={(filters && filters.comparator) || 'contains'}
                      onChange={(val) => dispatch(setMiscPublicSearchFilter('comparator', val))}
                      tabIndex={sharedTabIndex}
                    />
                  </DumbField>

                  <DumbField label="Registration Date" fieldId="registrationDate" type="DateRange" ownsLabel>
                    <div className="date-range-wrapper">
                      <DateRange
                        fieldId="registrationDate"
                        label="Registration Date"
                        startLabel="Start"
                        endLabel="End"
                        ownsLabel
                        value={{
                          start: (filters && filters.registrationDateStart) || '',
                          end: (filters && filters.registrationDateEnd) || '',
                        }}
                        onChange={(val) => {
                          dispatch(setMiscPublicSearchFilter('registrationDateStart', (val && val.start) || null))
                          dispatch(setMiscPublicSearchFilter('registrationDateEnd', (val && val.end) || null))
                        }}
                        onBlur={() => {}}
                        tabIndex={sharedTabIndex}
                      />
                    </div>
                  </DumbField>

                  {template && (template.SearchableFields || []).map(renderAdvancedField)}

                  <div className="advanced-toolbar">
                    <div className="flex-row">
                      <button
                        type="button"
                        className="btn btn-primary btn-raised advanced-search-button"
                        aria-disabled={!searchIsValid}
                        disabled={!searchIsValid}
                        onClick={handleSearch}
                        tabIndex={sharedTabIndex}
                      >
                        <div className="center">
                          <SearchIcon alt="Search Icon" /> Search
                        </div>
                      </button>
                      <button
                        type="button"
                        className="btn clear-button"
                        onClick={handleClearFilters}
                        tabIndex={sharedTabIndex}
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
              </div>

              <div className="advanced-search-toggle-wrapper">
                <button
                  ref={advancedToggleRef}
                  type="button"
                  className="btn btn-link advanced-toggle"
                  onClick={handleToggleAdvanced}
                  aria-expanded={!!advancedOpen}
                >
                  {'Advanced ' + (advancedOpen ? '▲' : '▼')}
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        </div>

        {!templateLoading && loading && (
          <div className="misc-public-search-loading">
            <Spinner size="medium" themeColor="secondary" />
          </div>
        )}

        {!templateLoading && !loading && !error && items && items.length === 0 && (
          <EmptySearchPlaceholder searchTerm={filters && filters.search} />
        )}

        {!templateLoading && !loading && (error || (items && items.length > 0)) && (
          <MiscellaneousRecordTable
            columns={template && template.Columns}
            items={items}
            totalCount={totalCount}
            error={error}
            selectedRecordId={selectedRecordId}
            onRowClick={handleRowClick}
          />
        )}
      </div>

      <MiscellaneousRecordDrawer
        show={!!selectedRecordId}
        record={selectedRecord}
        columns={template && template.Columns}
        onDismiss={handleDismissDrawer}
        drawerData={drawerData}
        drawerLoading={!!drawerLoading}
      />
    </Body>
  )
}

export default MiscellaneousPublicSearch
